import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Search, User, Shield, Ban, Eye, DollarSign, TrendingUp, AlertTriangle, CheckCircle, XCircle, Calendar, Globe, Smartphone, Mail, Clock, Activity, ChevronDown, RefreshCw, ChevronLeft, ChevronRight, Phone, ExternalLink } from 'lucide-react';
import { socket } from '../Containers/MainContainer';
import { MdJoinLeft, MdTimelapse } from 'react-icons/md';

const AlertModal = ({ show, title, onConfirm, onCancel, children }) => {
  if (!show) return null;

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 50,
      padding: '16px'
    }}>
      <div style={{
        background: '#161b22',
        border: '1px solid #30363d',
        borderRadius: '12px',
        padding: '16px',
        maxWidth: '384px',
        width: '100%'
      }}>
        <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '12px', color: '#e6edf3' }}>{title}</h3>
        <div style={{ marginBottom: '16px', color: '#c9d1d9', fontSize: '14px' }}>
          {children}
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={onCancel}
            style={{
              flex: 1,
              padding: '8px 12px',
              background: '#21262d',
              color: '#c9d1d9',
              borderRadius: '6px',
              fontSize: '14px',
              border: '1px solid #30363d',
              cursor: 'pointer',
              transition: 'background 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.background = '#30363d'}
            onMouseLeave={(e) => e.target.style.background = '#21262d'}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            style={{
              flex: 1,
              padding: '8px 12px',
              background: '#238636',
              border: '1px solid #2ea043',
              color: '#ffffff',
              borderRadius: '6px',
              fontSize: '14px',
              cursor: 'pointer',
              transition: 'background 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.background = '#2ea043'}
            onMouseLeave={(e) => e.target.style.background = '#238636'}
          >
            Proceed
          </button>
        </div>
      </div>
    </div>
  );
};

function PlutoUser({ second_page = 12345 }) {
  const [userData, setUserData] = useState({});
  const [fingerprints, setUserFingerPrints] = useState([]);
  const [userProfile, setUserProfile] = useState({});
  const [offersMade, setOffersMade] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [category, setCategory] = useState(0);
  const [rank, setRank] = useState(0);
  const [banRate, setBanRate] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [banReason, setBanReason] = useState('Fraudulent Behaviour');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [alert, setAlert] = useState({ show: false, user_id: -1, offer_id: -1, name: '' });
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [showAdvancedData, setShowAdvancedData] = useState(false);

  // Pagination states for advanced data
  const [fingerprintPage, setFingerprintPage] = useState(1);
  const [ipPage, setIpPage] = useState(1);
  const [advancedDataPerPage] = useState(5);

  const PaginationButton = ({ page, active, onClick, disabled, children }) => (
    <button
      onClick={() => onClick(page)}
      disabled={disabled}
      style={{
        padding: '4px',
        fontSize: '14px',
        fontWeight: '500',
        borderRadius: '6px',
        border: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        background: active ? '#238636' : '#0d1117',
        color: active ? '#ffffff' : disabled ? '#484f58' : '#c9d1d9',
        transition: 'background 0.2s'
      }}
    >
      {children || page}
    </button>
  );

  const getLevelColor = (level) => {
    if (level >= 8) return '#a855f7';
    if (level >= 5) return '#58a6ff';
    if (level >= 3) return '#3fb950';
    return '#8b949e';
  };

  const numberWithCommas = (x) => {
    return x?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") || '0';
  };

  const loadCategoryData = useCallback((cat, page = 1) => {
    setCategoryLoading(true);
    setOffersMade([]);

    socket.emit('getUserAdminData', {
      user: second_page,
      page: page,
      cat: cat
    });

    const handleCategoryResponse = (data) => {
      setCategoryLoading(false);
      setOffersMade(data.data);
      setTotalPages(Math.ceil(parseInt(data.length) / 5));
      setCurrentPage(page);
      socket.off('response_get_admin_user_data', handleCategoryResponse);
    };

    socket.on('response_get_admin_user_data', handleCategoryResponse);
  }, [second_page]);

  useEffect(() => {
    setLoading(true);

    socket.on('response_get_admin_user_fingerprints', (data) => {
      setUserFingerPrints(data.fingerprints);
    });

    socket.on('response_get_admin_user', (data) => {
      setUserData(data);
      setUserProfile(data.user);
      setLoading(false);
    });

    socket.on('response_get_admin_user_data', (data) => {
      setBanRate(data.banRate);
      setRank(data.rank);
    });

    socket.emit('getUserAdmin', { user_id: second_page });
    loadCategoryData(0, 1);

    return () => {
      socket.off('response_get_admin_user_fingerprints');
      socket.off('response_get_admin_user');
      socket.off('response_get_admin_user_data');
    };
  }, [second_page, loadCategoryData]);

  const handleCategoryChange = useCallback((newCategory) => {
    if (newCategory !== category) {
      setCategory(newCategory);
      setSearchTerm('');
      setStatusFilter('all');
      setCurrentPage(1);
      loadCategoryData(newCategory, 1);
    }
  }, [category, loadCategoryData]);

  const handlePageChange = useCallback((newPage) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage) {
      loadCategoryData(category, newPage);
    }
  }, [category, currentPage, totalPages, loadCategoryData]);

  // Pagination calculations for advanced data
  const paginatedFingerprints = useMemo(() => {
    const startIndex = (fingerprintPage - 1) * advancedDataPerPage;
    return fingerprints.slice(startIndex, startIndex + advancedDataPerPage);
  }, [fingerprints, fingerprintPage, advancedDataPerPage]);

  const paginatedIps = useMemo(() => {
    const startIndex = (ipPage - 1) * advancedDataPerPage;
    return userProfile.ipUsedData?.slice(startIndex, startIndex + advancedDataPerPage) || [];
  }, [userProfile.ipUsedData, ipPage, advancedDataPerPage]);

  const fingerprintTotalPages = Math.ceil(fingerprints.length / advancedDataPerPage);
  const ipTotalPages = Math.ceil((userProfile.ipUsedData?.length || 0) / advancedDataPerPage);

  const banReasons = [
    'Fraudulent Behaviour', 'Temp Email Address', 'Multiple Accounts (Same Username or Similar Email)',
    'Fraudulent Offers', 'Fraudulent Surveys', 'High Fraud Rate', 'High Chargeback Rate',
    'Multiple Accounts', 'VPN/Proxy Usage', 'Speedy Offers',
    'Country Phone Number Different Than Country Offers Completed',
    'Country Account Location Different Than Country Offers Completed',
    'Too many countries used for offers completion'
  ];

  const handleReleaseOffer = (userId, offerId, offerName) => {
    setAlert({
      show: true,
      user_id: userId,
      offer_id: offerId,
      name: offerName,
      title: 'Release Offer'
    });
  };

  const confirmReleaseOffer = () => {
    socket.emit('adminAction', {
      action: 'releaseOfferAdmin',
      user_id: alert.user_id,
      offer_id: alert.offer_id,
    });
    setAlert({ show: false, user_id: -1, offer_id: -1, name: '', title: '' });
    loadCategoryData(category, currentPage);
  };

  const handleAction = (action, additionalData = {}) => {
    socket.emit('adminAction', {
      action,
      user: userProfile.id,
      ...additionalData
    });
  };

  const renderStatusCell = (offer) => {
    if (category === 2) {
      if (offer.wall_name === "Direct Wallet") {
        return offer.paid === "No" ? 'None' : (
          <a
            href={`https://blockchair.com/${offer.name.toLowerCase().split(" ").join("-")}/transaction/${offer.status}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#58a6ff', fontSize: '12px', textDecoration: 'none' }}
          >
            TX <ExternalLink style={{ height: '12px', width: '12px', display: 'inline' }} />
          </a>
        );
      }
      return offer.status ? offer.status.substr(0, 4) + "..." : '';
    } else {
      if (offer.status === "Pending") {
        return (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ fontSize: '12px', color: '#d29922' }}>Pending</span>
              <button
                onClick={() => handleReleaseOffer(userProfile.id, offer.id, offer.name)}
                style={{
                  color: '#58a6ff',
                  fontSize: '12px',
                  textDecoration: 'underline',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                Release
              </button>
            </div>
            <span style={{ color: '#484f58', fontSize: '12px' }}>
              {offer.release_time?.split(' ').join('_')}
            </span>
          </div>
        );
      }
      return offer.status;
    }
  };

  const statColorMap = {
    blue: '#58a6ff',
    green: '#3fb950',
    red: '#f85149',
    orange: '#f97316',
    yellow: '#d29922',
    gray: '#8b949e',
    purple: '#a855f7',
    teal: '#14b8a6'
  };

  const StatCard = ({ title, value, icon: Icon, color = 'blue', alert = false }) => (
    <div style={{
      background: alert ? 'rgba(248,81,73,0.08)' : '#161b22',
      borderRadius: '12px',
      padding: '8px',
      border: alert ? '1px solid rgba(248,81,73,0.3)' : '1px solid #21262d',
      borderLeft: `3px solid ${alert ? '#f85149' : (statColorMap[color] || '#58a6ff')}`
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <p style={{ color: '#8b949e', fontSize: '12px', fontWeight: '500', margin: 0 }}>{title}</p>
          <p style={{
            fontWeight: 'bold',
            fontSize: '14px',
            margin: 0,
            color: alert ? '#f85149' : (statColorMap[color] || '#58a6ff')
          }}>
            {value}
          </p>
        </div>
        <Icon style={{
          color: alert ? '#f85149' : (statColorMap[color] || '#58a6ff'),
          height: '16px',
          width: '16px',
          flexShrink: 0
        }} />
      </div>
    </div>
  );

  const PaginationControls = ({ currentPage, totalPages, onPageChange, disabled = false }) => {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '12px' }}>
        <span style={{ color: '#8b949e' }}>Page {currentPage} of {totalPages}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1 || disabled}
            style={{
              padding: '4px',
              borderRadius: '4px',
              background: 'transparent',
              border: 'none',
              color: '#c9d1d9',
              cursor: (currentPage === 1 || disabled) ? 'not-allowed' : 'pointer',
              opacity: (currentPage === 1 || disabled) ? 0.5 : 1
            }}
          >
            <ChevronLeft style={{ height: '12px', width: '12px' }} />
          </button>

          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages || disabled}
            style={{
              padding: '4px',
              borderRadius: '4px',
              background: 'transparent',
              border: 'none',
              color: '#c9d1d9',
              cursor: (currentPage === totalPages || disabled) ? 'not-allowed' : 'pointer',
              opacity: (currentPage === totalPages || disabled) ? 0.5 : 1
            }}
          >
            <ChevronRight style={{ height: '12px', width: '12px' }} />
          </button>
        </div>
      </div>
    );
  };

  const renderPagination = () => {
    const pages = [];

    if (totalPages > 1) pages.push(1);

    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
      if (!pages.includes(i)) pages.push(i);
    }

    if (totalPages > 1 && !pages.includes(totalPages)) pages.push(totalPages);

    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', fontSize: '14px', color: '#c9d1d9' }}>
          <span style={{ color: '#8b949e' }}>Page {currentPage} of {totalPages}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <PaginationButton
            page={currentPage - 1}
            onClick={handlePageChange}
            disabled={currentPage === 1}
          >
            <ChevronLeft style={{ height: '16px', width: '16px' }} />
          </PaginationButton>

          {pages.map((page, index) => (
            <React.Fragment key={page}>
              {index > 0 && pages[index - 1] !== page - 1 && (
                <span style={{ padding: '0 8px', color: '#484f58' }}>...</span>
              )}
              <PaginationButton
                page={page}
                active={page === currentPage}
                onClick={handlePageChange}
              />
            </React.Fragment>
          ))}

          <PaginationButton
            page={currentPage + 1}
            onClick={handlePageChange}
            disabled={currentPage === totalPages}
          >
            <ChevronRight style={{ height: '16px', width: '16px' }} />
          </PaginationButton>
        </div>
      </div>
    );
  };

  const categories = [
    { id: 0, name: 'Offers', icon: String.fromCodePoint(0x1F4CB) },
    { id: 1, name: 'Earnings', icon: String.fromCodePoint(0x1F4B0) },
    { id: 2, name: 'Withdrawals', icon: String.fromCodePoint(0x1F4B3) },
    { id: 3, name: 'Chargebacks', icon: String.fromCodePoint(0x26A0, 0xFE0F) }
  ];

  const filteredOffers = useMemo(() => {
    return offersMade.filter(offer => {
      const matchesSearch = offer.name?.toLowerCase().includes(searchTerm.toLowerCase()) || false;
      const matchesStatus = statusFilter === 'all' || offer.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [offersMade, searchTerm, statusFilter]);

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#0d1117',
        color: '#e6edf3',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <RefreshCw className="animate-spin" style={{ height: '20px', width: '20px', color: '#58a6ff' }} />
          <span style={{ fontSize: '14px', color: '#c9d1d9' }}>Loading user data...</span>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#0d1117', color: '#e6edf3' }}>
      <AlertModal
        show={alert.show}
        title={alert.title}
        onConfirm={confirmReleaseOffer}
        onCancel={() => setAlert({ show: false, user_id: -1, offer_id: -1, name: '', title: '' })}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <p style={{ margin: 0 }}><strong>Offer:</strong> {alert.name}</p>
          <p style={{ margin: 0 }}><strong>Offer ID:</strong> {alert.offer_id}</p>
          <p style={{ margin: 0 }}><strong>User ID:</strong> {alert.user_id}</p>
        </div>
      </AlertModal>

      {/* Compact Mobile Header */}
      <div style={{
        background: '#161b22',
        borderBottom: '1px solid #21262d'
      }}>
        <div style={{ padding: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <img
              src={userProfile.profilePic || '/api/placeholder/40/40'}
              alt="Profile"
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                border: '2px solid #30363d'
              }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                <h1 style={{
                  fontSize: '18px',
                  fontWeight: 'bold',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  margin: 0,
                  color: '#e6edf3'
                }}>{userProfile.username}</h1>
                <span style={{
                  padding: '2px 8px',
                  borderRadius: '9999px',
                  fontSize: '12px',
                  fontWeight: '500',
                  background: getLevelColor(userProfile.level),
                  color: '#ffffff'
                }}>
                  L{userProfile.level}
                </span>
                {userProfile.banned && (
                  <span style={{
                    padding: '2px 8px',
                    borderRadius: '9999px',
                    fontSize: '12px',
                    fontWeight: '500',
                    background: '#f85149',
                    color: '#ffffff'
                  }}>
                    Banned
                  </span>
                )}
              </div>

              {/* Compact User Info */}
              <div style={{ fontSize: '12px', color: '#8b949e' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Mail style={{ height: '12px', width: '12px' }} />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '128px' }}>{userProfile.email}</span>
                    {userProfile.emailVerified && <CheckCircle style={{ height: '12px', width: '12px', color: '#3fb950' }} />}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Globe style={{ height: '12px', width: '12px' }} />
                    <span>{userProfile.country}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Phone style={{ height: '12px', width: '12px' }} />
                    <span>{userProfile.phone_verify}</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <MdJoinLeft style={{ height: '12px', width: '12px' }} />
                      <span>Joined {userProfile.joinDate}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <MdTimelapse style={{ height: '12px', width: '12px' }} />
                      <span>Online {userProfile.lastOnline} ago</span>
                    </div>
                  </div>
                </div>

                {/* Admin Actions */}
                {rank >= 8 && (
                  <div style={{ paddingTop: '8px' }}>
                    {/* Ban/Unban Section */}
                    <div style={{ display: 'flex' }}>
                      {!userProfile.banned && (
                        <select
                          value={banReason}
                          onChange={(e) => setBanReason(e.target.value)}
                          style={{
                            flex: 1,
                            background: '#0d1117',
                            border: '1px solid #30363d',
                            borderRadius: '4px',
                            padding: '4px 8px',
                            fontSize: '12px',
                            color: '#e6edf3'
                          }}
                        >
                          {banReasons.slice(0, 6).map(reason => (
                            <option key={reason} value={reason}>
                              {(reason + '').substring(0, 30)}...
                            </option>
                          ))}
                        </select>
                      )}
                      <button
                        onClick={() => handleAction('ban_user', { reason: banReason })}
                        style={{
                          borderRadius: '4px',
                          fontSize: '12px',
                          cursor: 'pointer',
                          border: 'none',
                          padding: '4px 8px',
                          transition: 'background 0.2s',
                          background: userProfile.banned ? '#238636' : '#f85149',
                          color: '#ffffff'
                        }}
                      >
                        {!userProfile.banned ? 'Ban' : 'Unban'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Expandable User Details */}
          {showUserDetails && (
            <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #21262d' }}>
              {/* Admin Actions */}
              {rank >= 8 && (
                <div style={{ paddingTop: '8px' }}>
                  {/* Additional Admin Actions */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    {/* Email Verification */}
                    <button
                      onClick={() => handleAction(userProfile.emailVerified ? 'unverify_email' : 'verify_email')}
                      style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        cursor: 'pointer',
                        border: 'none',
                        transition: 'background 0.2s',
                        background: userProfile.emailVerified ? '#f97316' : '#238636',
                        color: '#ffffff'
                      }}
                    >
                      {userProfile.emailVerified ? 'Unverify Email' : 'Verify Email'}
                    </button>

                    {/* Phone Verification */}
                    <button
                      onClick={() => handleAction(userProfile.phone_verify === 'Yes' ? 'unverify_phone' : 'verify_phone')}
                      style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        cursor: 'pointer',
                        border: 'none',
                        transition: 'background 0.2s',
                        background: userProfile.phone_verify === 'Yes' ? '#f97316' : '#238636',
                        color: '#ffffff'
                      }}
                    >
                      {userProfile.phone_verify === 'Yes' ? 'Unverify Phone' : 'Verify Phone'}
                    </button>

                    {/* Reset Password */}
                    <button
                      onClick={() => handleAction('reset_password')}
                      style={{
                        padding: '4px 8px',
                        background: '#1f6feb',
                        color: '#ffffff',
                        borderRadius: '4px',
                        fontSize: '12px',
                        cursor: 'pointer',
                        border: 'none',
                        transition: 'background 0.2s'
                      }}
                    >
                      Reset Password
                    </button>

                    {/* Clear Balance */}
                    <button
                      onClick={() => handleAction('clear_balance')}
                      style={{
                        padding: '4px 8px',
                        background: '#a855f7',
                        color: '#ffffff',
                        borderRadius: '4px',
                        fontSize: '12px',
                        cursor: 'pointer',
                        border: 'none',
                        transition: 'background 0.2s'
                      }}
                    >
                      Clear Balance
                    </button>

                    {/* Lock/Unlock Account */}
                    <button
                      onClick={() => handleAction(userProfile.locked ? 'unlock_account' : 'lock_account')}
                      style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        cursor: 'pointer',
                        border: 'none',
                        transition: 'background 0.2s',
                        background: userProfile.locked ? '#238636' : '#d29922',
                        color: '#ffffff'
                      }}
                    >
                      {userProfile.locked ? 'Unlock' : 'Lock'} Account
                    </button>

                    {/* Delete Account */}
                    <button
                      onClick={() => handleAction('delete_account')}
                      style={{
                        padding: '4px 8px',
                        background: '#da3633',
                        color: '#ffffff',
                        borderRadius: '4px',
                        fontSize: '12px',
                        cursor: 'pointer',
                        border: 'none',
                        transition: 'background 0.2s'
                      }}
                    >
                      Delete Account
                    </button>
                  </div>

                  {/* Additional Tools */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginTop: '8px' }}>
                    <button
                      onClick={() => handleAction('reset_level')}
                      style={{
                        padding: '4px 8px',
                        background: '#21262d',
                        border: '1px solid #30363d',
                        color: '#c9d1d9',
                        borderRadius: '4px',
                        fontSize: '12px',
                        cursor: 'pointer',
                        transition: 'background 0.2s'
                      }}
                    >
                      Reset Level
                    </button>
                    <button
                      onClick={() => handleAction('clear_referrals')}
                      style={{
                        padding: '4px 8px',
                        background: '#21262d',
                        border: '1px solid #30363d',
                        color: '#c9d1d9',
                        borderRadius: '4px',
                        fontSize: '12px',
                        cursor: 'pointer',
                        transition: 'background 0.2s'
                      }}
                    >
                      Clear Refs
                    </button>
                    <button
                      onClick={() => handleAction('force_logout')}
                      style={{
                        padding: '4px 8px',
                        background: '#21262d',
                        border: '1px solid #30363d',
                        color: '#c9d1d9',
                        borderRadius: '4px',
                        fontSize: '12px',
                        cursor: 'pointer',
                        transition: 'background 0.2s'
                      }}
                    >
                      Force Logout
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {/* Compact Stats Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
          <StatCard
            title="Balance"
            value={`$${numberWithCommas(userProfile.balance)}`}
            icon={DollarSign}
            color="green"
          />
          <StatCard
            title="Earned"
            value={`$${numberWithCommas(userProfile.totalEarned)}`}
            icon={TrendingUp}
            color="blue"
          />
          <StatCard
            title="Withdrawn"
            value={`$${numberWithCommas(userProfile.withdrawAmount)}`}
            icon={DollarSign}
            color="purple"
          />
          <StatCard
            title="Ban Rate"
            value={`${banRate}%`}
            icon={AlertTriangle}
            color={banRate > 10 ? "red" : "yellow"}
            alert={banRate > 10}
          />
          <StatCard
            title="CB Rate"
            value={`${userData.cbRate}%`}
            icon={AlertTriangle}
            color="red"
            alert={userData.cbRate > 20}
          />
          <StatCard
            title="VPN"
            value={userProfile.usedVPNForOffers === 'Yes' ? 'Yes' : 'No'}
            icon={Shield}
            color={userProfile.usedVPNForOffers === 'Yes' ? 'red' : 'green'}
            alert={userProfile.usedVPNForOffers === 'Yes'}
          />
        </div>

        {/* Ban Alert */}
        {userProfile.banned && (
          <div style={{
            background: 'rgba(248,81,73,0.08)',
            border: '1px solid rgba(248,81,73,0.3)',
            borderRadius: '12px',
            padding: '12px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
              <Ban style={{ height: '16px', width: '16px', color: '#f85149', flexShrink: 0 }} />
              <span style={{ fontWeight: '500', color: '#f85149' }}>Banned:</span>
              <span style={{ color: '#c9d1d9', fontSize: '12px' }}>{userProfile.banReason}</span>
            </div>
          </div>
        )}

        {/* Data Section */}
        <div style={{
          background: '#161b22',
          borderRadius: '12px',
          border: '1px solid #21262d',
          overflow: 'hidden'
        }}>
          {/* Category Tabs - Mobile Friendly */}
          <div style={{ borderBottom: '1px solid #21262d', padding: '12px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '4px' }}>
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => handleCategoryChange(cat.id)}
                  disabled={categoryLoading}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '8px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    cursor: categoryLoading ? 'not-allowed' : 'pointer',
                    border: 'none',
                    transition: 'all 0.2s',
                    background: category === cat.id ? '#238636' : 'rgba(33,38,45,0.5)',
                    color: category === cat.id ? '#ffffff' : '#c9d1d9',
                    opacity: categoryLoading ? 0.5 : 1
                  }}
                >
                  <span style={{ fontSize: '14px' }}>{cat.icon}</span>
                  <span>{cat.name}</span>
                  {categoryLoading && category === cat.id && (
                    <RefreshCw className="animate-spin" style={{ height: '12px', width: '12px' }} />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Search and Filter - Compact */}
          <div style={{ padding: '12px', borderBottom: '1px solid #21262d' }}>
            <div style={{ display: 'flex', gap: '8px' }}>
              <div style={{ flex: 1, position: 'relative' }}>
                <Search style={{
                  position: 'absolute',
                  left: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  height: '12px',
                  width: '12px',
                  color: '#8b949e'
                }} />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: '100%',
                    paddingLeft: '28px',
                    paddingRight: '12px',
                    paddingTop: '8px',
                    paddingBottom: '8px',
                    background: '#0d1117',
                    border: '1px solid #30363d',
                    borderRadius: '6px',
                    fontSize: '14px',
                    color: '#e6edf3',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#58a6ff'}
                  onBlur={(e) => e.target.style.borderColor = '#30363d'}
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{
                  background: '#0d1117',
                  border: '1px solid #30363d',
                  borderRadius: '6px',
                  padding: '8px',
                  fontSize: '14px',
                  color: '#e6edf3'
                }}
              >
                <option value="all">All</option>
                <option value="Pending">Pending</option>
                <option value="Credited">Credited</option>
                <option value="Rejected">Rejected</option>
              </select>
            </div>
          </div>

          {/* Mobile Cards / Desktop Table */}
          <div style={{ overflow: 'hidden' }}>
            {categoryLoading ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px 0' }}>
                <RefreshCw className="animate-spin" style={{ height: '20px', width: '20px', color: '#58a6ff' }} />
                <span style={{ marginLeft: '8px', color: '#8b949e', fontSize: '14px' }}>Loading...</span>
              </div>
            ) : (
              <>
                {/* Mobile Cards */}
                <div className="md:hidden" style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {filteredOffers.map((offer, i) => (
                    <div key={offer.id || i} style={{
                      background: 'rgba(33,38,45,0.3)',
                      borderRadius: '8px',
                      padding: '12px',
                      border: '1px solid #21262d'
                    }}>
                      {/* Header with Name and Amount */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                        <h4 style={{
                          fontWeight: '500',
                          fontSize: '14px',
                          color: '#e6edf3',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: 1,
                          marginRight: '8px',
                          margin: 0
                        }} title={offer.name}>
                          {offer.name}
                        </h4>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ color: '#3fb950', fontWeight: '600', fontSize: '14px' }}>
                            {offer.amount.split('(')[0]}
                          </div>
                          {offer.amount.includes('(') && (
                            <div style={{ color: '#484f58', fontSize: '12px' }}>
                              {offer.amount.split('(')[1].split(')')[0]}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Basic Info Grid */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '12px', marginBottom: '8px' }}>
                        <div>
                          <span style={{ color: '#484f58' }}>Date:</span>
                          <span style={{ color: '#c9d1d9', marginLeft: '4px' }}>{offer.date}</span>
                        </div>
                        <div>
                          <span style={{ color: '#484f58' }}>Wall:</span>
                          <span style={{ color: '#c9d1d9', marginLeft: '4px' }} title={offer.wall_name}>
                            {offer.wall_name}
                          </span>
                        </div>
                      </div>

                      {/* Category-specific data */}
                      {(category === 0 || category === 3) && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '12px', marginBottom: '8px' }}>
                          <div>
                            <span style={{ color: '#484f58' }}>CB Rate:</span>
                            <span style={{
                              marginLeft: '4px',
                              color: parseFloat(offer.cb) > 20 ? '#f85149' :
                                parseFloat(offer.cb) > 10 ? '#d29922' : '#3fb950'
                            }}>
                              {offer.cbRate}
                            </span>
                          </div>
                          <div>
                            <span style={{ color: '#484f58' }}>Ban Rate:</span>
                            <span style={{ color: '#c9d1d9', marginLeft: '4px' }}>{offer.banRate}</span>
                          </div>
                          <div>
                            <span style={{ color: '#484f58' }}>Flag:</span>
                            <span style={{ marginLeft: '4px' }}>
                              <span style={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                display: 'inline-block',
                                background: offer.shouldBanX ? '#f85149' : '#3fb950'
                              }}></span>
                              <span style={{ marginLeft: '4px', color: '#c9d1d9' }}>
                                {offer.shouldBanX ? 'Flagged' : 'Clean'}
                              </span>
                            </span>
                          </div>
                          <div>
                            <span style={{ color: '#484f58' }}>Country:</span>
                            <span style={{ color: '#c9d1d9', marginLeft: '4px' }}>{offer.ip}</span>
                          </div>
                        </div>
                      )}

                      {/* Status and Verification Row */}
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        paddingTop: '8px',
                        borderTop: '1px solid #21262d'
                      }}>
                        <div style={{ fontSize: '12px' }}>
                          <div style={{ color: '#484f58', marginBottom: '4px' }}>Status:</div>
                          <div style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: '500',
                            ...(offer.status === 'Credited' ? { background: 'rgba(63,185,80,0.15)', color: '#3fb950' } :
                              offer.status === 'Pending' ? { background: 'rgba(210,153,34,0.15)', color: '#d29922' } :
                                offer.status === 'Completed' ? { background: 'rgba(88,166,255,0.15)', color: '#58a6ff' } :
                                  { background: 'rgba(248,81,73,0.15)', color: '#f85149' })
                          }}>
                            {renderStatusCell(offer)}
                          </div>
                        </div>

                        {/* Verification section for offers/chargebacks */}
                        {(category === 0 || category === 3) && (
                          <div style={{ fontSize: '12px', textAlign: 'right' }}>
                            <div style={{ color: '#484f58', marginBottom: '4px' }}>Verified:</div>
                            <div style={{ color: offer.verified ? '#3fb950' : '#d29922' }}>
                              <span style={{ display: 'block', marginBottom: '4px' }}>
                                {offer.verified ? '\u2713 Yes' : '\u2717 No'}
                              </span>
                              <div
                                onClick={() => {
                                  const action = offer.verified ? 'remove_trustedoffer' : 'add_trustedoffer';
                                  socket.emit('adminAction', {
                                    action,
                                    user: userProfile.id,
                                    name: offer.name,
                                    wall: offer.wall_name,
                                  });
                                  loadCategoryData(category, currentPage);
                                }}
                                style={{
                                  color: '#58a6ff',
                                  fontSize: '12px',
                                  textDecoration: 'underline',
                                  cursor: 'pointer'
                                }}
                              >
                                {offer.verified ? 'Remove' : 'Add'}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* For withdrawals - show transaction link if available */}
                        {category === 2 && offer.wall_name === "Direct Wallet" && offer.paid !== "No" && (
                          <div style={{ fontSize: '12px' }}>
                            <a
                              href={`https://blockchair.com/${offer.name.toLowerCase().split(" ").join("-")}/transaction/${offer.status}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: '#58a6ff',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                textDecoration: 'none'
                              }}
                            >
                              View TX <ExternalLink style={{ height: '12px', width: '12px' }} />
                            </a>
                          </div>
                        )}
                      </div>

                      {/* Additional details for specific categories */}
                      {category === 2 && offer.status && offer.wall_name !== "Direct Wallet" && (
                        <div style={{ paddingTop: '8px', borderTop: '1px solid #21262d', marginTop: '8px' }}>
                          <div style={{ fontSize: '12px' }}>
                            <span style={{ color: '#484f58' }}>Transaction:</span>
                            <span style={{
                              color: '#c9d1d9',
                              marginLeft: '4px',
                              fontFamily: 'monospace',
                              fontSize: '12px',
                              wordBreak: 'break-all'
                            }}>
                              {offer.status.length > 20 ? `${offer.status.substr(0, 20)}...` : offer.status}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Release time for pending offers */}
                      {offer.status === "Pending" && offer.release_time && (
                        <div style={{ paddingTop: '8px', borderTop: '1px solid #21262d', marginTop: '8px' }}>
                          <div style={{ fontSize: '12px' }}>
                            <span style={{ color: '#484f58' }}>Release Time:</span>
                            <span style={{ color: '#8b949e', marginLeft: '4px' }}>
                              {offer.release_time.split(' ').join('_')}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Desktop Table */}
                <div className="hidden md:block" style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #21262d', background: '#161b22' }}>
                        <th style={{
                          textAlign: 'left',
                          padding: '8px 12px',
                          fontWeight: '600',
                          fontSize: '11px',
                          color: '#8b949e',
                          textTransform: 'uppercase',
                          letterSpacing: '0.8px'
                        }}>Name</th>
                        {(category === 0 || category === 3) && (
                          <>
                            <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: '600', fontSize: '11px', color: '#8b949e', textTransform: 'uppercase', letterSpacing: '0.8px' }}>CB%</th>
                            <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: '600', fontSize: '11px', color: '#8b949e', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Ban%</th>
                            <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: '600', fontSize: '11px', color: '#8b949e', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Flag</th>
                          </>
                        )}
                        <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: '600', fontSize: '11px', color: '#8b949e', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Amount</th>
                        <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: '600', fontSize: '11px', color: '#8b949e', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Date</th>
                        <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: '600', fontSize: '11px', color: '#8b949e', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Wall</th>
                        <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: '600', fontSize: '11px', color: '#8b949e', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Status</th>
                        {(category === 0 || category === 3) && (
                          <>
                            <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: '600', fontSize: '11px', color: '#8b949e', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Country</th>
                            <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: '600', fontSize: '11px', color: '#8b949e', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Verified</th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredOffers.map((offer, i) => (
                        <tr
                          key={offer.id || i}
                          style={{
                            borderBottom: '1px solid #21262d',
                            transition: 'background 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(33,38,45,0.5)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                          <td style={{
                            padding: '8px 12px',
                            fontWeight: '500',
                            maxWidth: '200px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: '#e6edf3'
                          }} title={offer.name}>
                            {offer.name}
                          </td>
                          {(category === 0 || category === 3) && (
                            <>
                              <td style={{
                                padding: '8px 12px',
                                color: parseFloat(offer.cb) > 20 ? '#f85149' :
                                  parseFloat(offer.cb) > 10 ? '#d29922' : '#3fb950'
                              }}>
                                {offer.cbRate}
                              </td>
                              <td style={{ padding: '8px 12px', color: '#c9d1d9' }}>{offer.banRate}</td>
                              <td style={{ padding: '8px 12px' }}>
                                <span style={{
                                  width: '8px',
                                  height: '8px',
                                  borderRadius: '50%',
                                  display: 'inline-block',
                                  background: offer.shouldBanX ? '#f85149' : '#3fb950'
                                }}></span>
                              </td>
                            </>
                          )}
                          <td style={{ padding: '8px 12px', fontWeight: '600' }}>
                            <div style={{ color: '#3fb950' }}>{offer.amount.split('(')[0]}</div>
                            {offer.amount.includes('(') && (
                              <div style={{ color: '#484f58' }}>{offer.amount.split('(')[1].split(')')[0]}</div>
                            )}
                          </td>
                          <td style={{ padding: '8px 12px', color: '#8b949e' }}>{offer.date}</td>
                          <td style={{
                            padding: '8px 12px',
                            maxWidth: '100px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: '#c9d1d9'
                          }} title={offer.wall_name}>
                            {offer.wall_name}
                          </td>
                          <td style={{ padding: '8px 12px', color: '#c9d1d9' }}>
                            {renderStatusCell(offer)}
                          </td>
                          {(category === 0 || category === 3) && (
                            <>
                              <td style={{
                                padding: '8px 12px',
                                maxWidth: '70px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                color: '#c9d1d9'
                              }}>{offer.ip}</td>
                              <td style={{ padding: '8px 12px' }}>
                                <div style={{ color: offer.verified ? '#3fb950' : '#d29922' }}>
                                  {offer.verified ? 'Yes ' : 'No '}
                                  <button
                                    onClick={() => {
                                      const action = offer.verified ? 'remove_trustedoffer' : 'add_trustedoffer';
                                      socket.emit('adminAction', {
                                        action,
                                        user: userProfile.id,
                                        name: offer.name,
                                        wall: offer.wall_name,
                                      });
                                      loadCategoryData(category, currentPage);
                                    }}
                                    style={{
                                      color: '#58a6ff',
                                      fontSize: '12px',
                                      textDecoration: 'underline',
                                      background: 'none',
                                      border: 'none',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    {offer.verified ? 'Remove' : 'Add'}
                                  </button>
                                </div>
                              </td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {filteredOffers.length === 0 && !categoryLoading && (
              <div style={{ padding: '32px 12px', textAlign: 'center', color: '#8b949e', fontSize: '14px' }}>
                No data found
              </div>
            )}
          </div>

          {/* Compact Pagination */}
          {renderPagination()}
        </div>

        {/* Advanced Data Toggle */}
        {rank >= 8 && (fingerprints.length > 0 || userProfile.ipUsedData?.length > 0) && (
          <button
            onClick={() => setShowAdvancedData(!showAdvancedData)}
            style={{
              width: '100%',
              background: '#161b22',
              border: '1px solid #21262d',
              borderRadius: '12px',
              padding: '12px',
              textAlign: 'left',
              cursor: 'pointer',
              transition: 'background 0.2s',
              color: '#e6edf3'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(33,38,45,0.5)'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#161b22'}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '14px', fontWeight: '500' }}>Advanced Data</span>
              <ChevronDown style={{
                height: '16px',
                width: '16px',
                transition: 'transform 0.2s',
                transform: showAdvancedData ? 'rotate(180deg)' : 'rotate(0deg)'
              }} />
            </div>
          </button>
        )}

        {/* Advanced Data - Collapsible */}
        {true === true && rank >= 8 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {/* Fingerprints */}
            {fingerprints.length > 0 && (
              <div style={{
                background: '#161b22',
                borderRadius: '12px',
                border: '1px solid #21262d',
                overflow: 'hidden'
              }}>
                <div style={{ padding: '12px', borderBottom: '1px solid #21262d' }}>
                  <h3 style={{
                    fontSize: '14px',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    margin: 0,
                    color: '#e6edf3'
                  }}>
                    <Activity style={{ height: '16px', width: '16px', marginRight: '8px', color: '#58a6ff' }} />
                    Device Fingerprints ({fingerprints.length})
                  </h3>
                </div>

                <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {paginatedFingerprints.map((fp, index) => (
                    <div key={fp.id || index} style={{
                      background: 'rgba(33,38,45,0.3)',
                      borderRadius: '6px',
                      padding: '8px',
                      fontSize: '12px',
                      border: '1px solid #21262d'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{
                            fontFamily: 'monospace',
                            color: '#c9d1d9',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            marginBottom: '4px'
                          }} title={fp.fingerprint}>
                            {(fp.fingerprint + '').substring(0, 40)}...
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#484f58' }}>
                            <span>IP: {fp.ip}</span>
                            <span>&#8226;</span>
                            <span>Count: {fp.count}</span>
                          </div>
                        </div>
                        <a
                          href={`/pluto/fingerprint/${encodeURIComponent(fp.fingerprint)}`}
                          target="_blank"
                          style={{
                            color: '#58a6ff',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            marginLeft: '8px',
                            flexShrink: 0,
                            textDecoration: 'none'
                          }}
                        >
                          <span>View</span>
                          <ExternalLink style={{ height: '12px', width: '12px' }} />
                        </a>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Fingerprints Pagination */}
                {fingerprintTotalPages > 1 && (
                  <div style={{ borderTop: '1px solid #21262d', padding: '12px' }}>
                    <PaginationControls
                      currentPage={fingerprintPage}
                      totalPages={fingerprintTotalPages}
                      onPageChange={setFingerprintPage}
                    />
                  </div>
                )}
              </div>
            )}

            {/* IP Data */}
            {userProfile.ipUsedData?.length > 0 && (
              <div style={{
                background: '#161b22',
                borderRadius: '12px',
                border: '1px solid #21262d',
                overflow: 'hidden'
              }}>
                <div style={{ padding: '12px', borderBottom: '1px solid #21262d' }}>
                  <h3 style={{
                    fontSize: '14px',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    margin: 0,
                    color: '#e6edf3'
                  }}>
                    <Globe style={{ height: '16px', width: '16px', marginRight: '8px', color: '#58a6ff' }} />
                    IP Usage Data ({userProfile.ipUsedData.length})
                  </h3>
                </div>

                <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {paginatedIps.map((ipData, index) => (
                    <div key={ipData.id || index} style={{
                      background: 'rgba(33,38,45,0.3)',
                      borderRadius: '6px',
                      padding: '8px',
                      fontSize: '12px',
                      border: '1px solid #21262d'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                            <span style={{ fontFamily: 'monospace', color: '#c9d1d9' }}>{ipData.ip}</span>
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              ...(ipData.ipData.isVPN
                                ? { background: 'rgba(248,81,73,0.15)', color: '#f85149' }
                                : { background: 'rgba(63,185,80,0.15)', color: '#3fb950' })
                            }}>
                              {ipData.ipData.isVPN ? 'VPN' : 'Clean'}
                            </span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#484f58' }}>
                            <span>Score: {ipData.ipData.score}</span>
                            <span>&#8226;</span>
                            <span>Users: {ipData.user.length}</span>
                          </div>
                        </div>
                        <a
                          href={`/pluto/ip/${encodeURIComponent(ipData.ip.split(' (')[0])}`}
                          target="_blank"
                          style={{
                            color: '#58a6ff',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            marginLeft: '8px',
                            flexShrink: 0,
                            textDecoration: 'none'
                          }}
                        >
                          <span>View</span>
                          <ExternalLink style={{ height: '12px', width: '12px' }} />
                        </a>
                      </div>
                    </div>
                  ))}
                </div>

                {/* IP Pagination */}
                {ipTotalPages > 1 && (
                  <div style={{ borderTop: '1px solid #21262d', padding: '12px' }}>
                    <PaginationControls
                      currentPage={ipPage}
                      totalPages={ipTotalPages}
                      onPageChange={setIpPage}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default PlutoUser;
